import {
  memo,
  useState,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  ChangeEvent,
} from 'react';
import { Image } from 'lucide-react';
import styled from 'styled-components';

import { useCurrentUser } from '@redux/selectors/users';
import { useGenerateUserAvatarMutation } from '@redux/queries/user';
import theme from 'theme';
import { DefaultText, PremierText } from '@components/text';
import { StyledErrorContainer, StyledErrorText } from '@components/form/styled';
import TouchableOpacity from '@components/buttons/TouchableOpacity';
import ProgressBar from '@components/progressBar';
import SkewedButton from '@components/buttons/SkewedButton';
import cardImage from '@assets/images/card.png';
import Select from '@components/fields/select';

const Container = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: center;
  gap: 8px;
`;

const HintText = styled(DefaultText)`
  max-width: 400px;
  font-size: ${({ theme }) => theme.fonts.fontSize.sm};
  color: ${({ theme }) => theme.colors.grey[200]};
`;

const ImageContainer = styled.div`
  width: 400px;
  height: 400px;
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 8px;
  border: 0.5px solid ${({ theme }) => theme.colors.grey[200]};

  @media (max-width: ${theme.breakpoints.md}) {
    width: 300px;
    height: 300px;
  }
`;

const BackgroundImage = styled.img`
  position: absolute;
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 8px;
`;

const GeneratedImage = styled.img`
  position: absolute;
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 8px;
`;

const ProgressBarContainer = styled.div`
  width: 100%;
  position: absolute;
  align-content: center;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  background-color: ${({ theme }) =>
    `${theme.colors.black}${theme.opacity[60]}`};
  border-radius: 8px;
`;

const LoadingText = styled(DefaultText)`
  font-size: ${({ theme }) => theme.fonts.fontSize.sm};
  color: ${({ theme }) => theme.colors.grey[200]};
`;

const UploadButton = styled(TouchableOpacity)`
  position: absolute;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 12px 16px;
  gap: 8px;
  background-color: ${({ theme }) =>
    `${theme.colors.black}${theme.opacity[40]}`};
  border-radius: 8px;
  color: ${({ theme }) => theme.colors.grey[200]};
`;

const HiddenFileInput = styled.input`
  display: none;
`;

const ButtonsContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  margin-top: 8px;
  gap: 16px;
`;

const ChangePhotoText = styled(PremierText)<{ $isDisabled?: boolean }>`
  font-size: ${({ theme }) => theme.fonts.fontSize.base};
  color: ${({ theme, $isDisabled }) =>
    $isDisabled ? theme.colors.grey[400] : theme.colors.white};
`;

const StyledSkewedButton = styled(SkewedButton)`
  width: 100%;
`;

const GENDER_OPTIONS = [
  { label: 'Male', value: 'male' },
  { label: 'Female', value: 'female' },
];

type Props = {
  handleProcessing: (isProcessing: boolean) => void;
};

const ProfileImage = ({ handleProcessing }: Props) => {
  const user = useCurrentUser();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploadedFile, setUploadedFile] = useState<File | undefined>();
  const [uploadedImageUrl, setUploadedImageUrl] = useState<string | null>(null);
  const [generated, setGenerated] = useState<{
    image?: string | null;
    background?: string | null;
  } | null>({
    image: null,
    background: null,
  });
  const [selectedGender, setSelectedGender] = useState<string>('');
  const [isProgressBarVisible, setIsProgressBarVisible] =
    useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const [generateAvatar, { isLoading, isSuccess, isError }] =
    useGenerateUserAvatarMutation();
  const isButtonsDisabled = useMemo(
    () => isLoading || isProgressBarVisible,
    [isLoading, isProgressBarVisible],
  );

  const startGeneration = useCallback(() => {
    if (uploadedFile && typeof uploadedFile !== 'string' && selectedGender) {
      generateAvatar({
        photo: uploadedFile,
        gender: selectedGender,
      });
    } else {
      setError('Please select a gender and upload an image.');
    }
  }, [generateAvatar, uploadedFile, selectedGender]);

  const handleImageChange = useCallback(
    (image: File) => {
      const maxSizeInBytes = 9 * 1024 * 1024;

      if (image.size > maxSizeInBytes) {
        setError(
          'File size must be less than 10MB. Please choose a smaller image.',
        );
        return;
      }

      setError('');

      if (uploadedImageUrl) {
        URL.revokeObjectURL(uploadedImageUrl);
      }
      const imageUrl = URL.createObjectURL(image);
      setUploadedImageUrl(imageUrl);
      setUploadedFile(image);
      setGenerated(null);
    },
    [uploadedImageUrl],
  );

  const handleGenderSelect = useCallback(
    (currentGender: string) => {
      setSelectedGender(currentGender);
    },
    [setSelectedGender],
  );

  const onGeneratePress = useCallback(() => {
    startGeneration();
  }, [startGeneration]);

  const onImageChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      if (e.target.files) {
        handleImageChange(e.target.files[0]);
      }
    },
    [handleImageChange],
  );

  const onUploadButtonClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const onLoadComplete = useCallback(() => {
    setIsProgressBarVisible(false);
    handleProcessing(false);
  }, [handleProcessing]);

  useEffect(() => {
    if (isLoading) {
      handleProcessing(true);
      setIsProgressBarVisible(true);
    }
  }, [isLoading, handleProcessing]);

  useEffect(() => {
    if (isError) {
      onLoadComplete();
      setError('Failed. Please, try again later.');
    }
  }, [isError, onLoadComplete]);

  useEffect(() => {
    if (isSuccess && user?.image && user?.background) {
      setGenerated({ image: user.image, background: user.background });
    }
  }, [isSuccess, setGenerated, user?.image, user?.background]);

  useEffect(() => {
    return () => {
      if (uploadedImageUrl) {
        URL.revokeObjectURL(uploadedImageUrl);
      }
    };
  }, [uploadedImageUrl]);

  return (
    <Container>
      <HintText>
        {
          'Time for your official squad photo, stand in a well-lit area and take a picture from the chest up. You can face the camera directly or turn slightly to capture your best angle.'
        }
      </HintText>

      <ImageContainer>
        {!!generated?.image && (
          <BackgroundImage
            src={generated?.background ? generated.background : cardImage}
          />
        )}

        {(generated?.image || uploadedImageUrl) && (
          <GeneratedImage
            src={generated?.image || uploadedImageUrl || undefined}
          />
        )}

        {isProgressBarVisible && (
          <ProgressBarContainer>
            <ProgressBar isProcessing={isLoading} onComplete={onLoadComplete} />

            <LoadingText>
              {'This may take a few minutes as we generate your unique look.'}
            </LoadingText>
          </ProgressBarContainer>
        )}

        {!isButtonsDisabled && !generated?.image && (
          <UploadButton
            onClick={onUploadButtonClick}
            disabled={isButtonsDisabled}
          >
            <HiddenFileInput
              ref={fileInputRef}
              onChange={onImageChange}
              type='file'
              accept='image/*'
            />
            <Image size={24} color={theme.colors.grey[200]} />
            Upload
          </UploadButton>
        )}
      </ImageContainer>

      <Select
        options={GENDER_OPTIONS}
        onChange={(newValue) => handleGenderSelect(newValue as string)}
        value={selectedGender}
        isDisabled={isButtonsDisabled}
      />
      <ButtonsContainer>
        {generated?.image ? (
          <>
            <StyledSkewedButton
              onClick={onUploadButtonClick}
              disabled={isButtonsDisabled}
              isLoading={isButtonsDisabled}
              isBorder
            >
              <HiddenFileInput
                ref={fileInputRef}
                onChange={onImageChange}
                type='file'
                accept='image/*'
              />
              <ChangePhotoText $isDisabled={isButtonsDisabled}>
                Change photo
              </ChangePhotoText>
            </StyledSkewedButton>

            <StyledSkewedButton
              onClick={onGeneratePress}
              disabled={isButtonsDisabled}
              isLoading={isButtonsDisabled}
              isBorder
            >
              Regenerate
            </StyledSkewedButton>
          </>
        ) : (
          <StyledSkewedButton
            onClick={onGeneratePress}
            disabled={isButtonsDisabled || !selectedGender || !uploadedFile}
            isLoading={isButtonsDisabled}
          >
            Generate
          </StyledSkewedButton>
        )}
      </ButtonsContainer>

      <StyledErrorContainer>
        {!!error && <StyledErrorText>{error}</StyledErrorText>}
      </StyledErrorContainer>
    </Container>
  );
};

export default memo(ProfileImage);
